import { Nullable } from '@/types/utils'
import { IProfileFields, IProfileSeed } from '@/entities/Profile'
import { isYear, isMonth, isDay, isRuby, isTel, isZipcode, isPersonName } from '@/utils/validators/helper'
import { prefectures } from '@/const'

export type ProfileFields = Nullable<IProfileFields & IProfileSeed>

const NAME_LENGTH_LIMIT = 33
const NICKNAME_LENGTH_LIMIT = 9
const ADDRESS_LENGTH_LIMIT = 64

export default class ProfileValidator {
  constructor(private fields: Partial<ProfileFields>) {}

  // 漢字／ひらがな、全角カナ、半角カナ、全角英字、半角英字、全角数字、半角数字
  isFirstnameFormatValid(): boolean {
    const { firstname } = this.fields
    return !!firstname && isPersonName(firstname)
  }

  // 名が33文字未満
  isFirstnameLengthValid(): boolean {
    const { firstname } = this.fields
    return !!firstname && firstname.length < NAME_LENGTH_LIMIT
  }

  isFirstnameValid(): boolean {
    return this.isFirstnameFormatValid() && this.isFirstnameLengthValid()
  }

  // 漢字／ひらがな、全角カナ、半角カナ、全角英字、半角英字、全角数字、半角数字
  isLastnameFormatValid(): boolean {
    const { lastname } = this.fields
    return !!lastname && isPersonName(lastname)
  }

  // 姓が33文字未満
  isLastnameLengthValid(): boolean {
    const { lastname } = this.fields
    return !!lastname && lastname.length < NAME_LENGTH_LIMIT
  }

  isLastnameValid(): boolean {
    return this.isLastnameFormatValid() && this.isLastnameLengthValid()
  }

  isFirstnameRubyFormatValid(): boolean {
    const { firstnameRuby } = this.fields
    return !!firstnameRuby && isRuby(firstnameRuby)
  }

  // 名が33文字未満
  isFirstnameRubyLengthValid(): boolean {
    const { firstnameRuby } = this.fields
    return !!firstnameRuby && firstnameRuby.length < NAME_LENGTH_LIMIT
  }

  isFirstnameRubyValid(): boolean {
    return this.isFirstnameRubyFormatValid() && this.isFirstnameRubyLengthValid()
  }

  isLastnameRubyFormatValid(): boolean {
    const { lastnameRuby } = this.fields
    return !!lastnameRuby && isRuby(lastnameRuby)
  }

  // 姓が33文字未満
  isLastnameRubyLengthValid(): boolean {
    const { lastnameRuby } = this.fields
    return !!lastnameRuby && lastnameRuby.length < NAME_LENGTH_LIMIT
  }

  isLastnameRubyValid(): boolean {
    return this.isLastnameRubyFormatValid() && this.isLastnameRubyLengthValid()
  }

  // ニックネームが9文字未満
  isNicknameLengthValid(): boolean {
    const { nickname } = this.fields
    return !!nickname && nickname.length < NICKNAME_LENGTH_LIMIT
  }

  isBirthYearValid(): boolean {
    const { birthYear } = this.fields
    return !!birthYear && isYear(birthYear)
  }

  isBirthMonthValid(): boolean {
    const { birthMonth } = this.fields
    return !!birthMonth && isMonth(birthMonth)
  }

  isBirthDayValid(): boolean {
    const { birthDay } = this.fields
    return !!birthDay && isDay(birthDay)
  }

  isSexValid(): boolean {
    return !!this.fields.sex && ['f', 'm'].includes(this.fields.sex)
  }

  isTelValid() {
    const { tel } = this.fields
    return !!tel && isTel(tel)
  }

  isZipcodeValid(): boolean {
    const { zipcode } = this.fields
    return !!zipcode && isZipcode(zipcode)
  }

  isPrefectureValid(): boolean {
    const { prefecture } = this.fields
    return !!prefecture && prefectures.includes(prefecture)
  }

  isAddress1LengthValid(): boolean {
    const { address1 } = this.fields
    return !!address1 && address1.length <= ADDRESS_LENGTH_LIMIT
  }

  isAddress2LengthValid(): boolean {
    const { address2 } = this.fields
    return !!address2 && address2.length <= ADDRESS_LENGTH_LIMIT
  }

  isAddress3LengthValid(): boolean {
    const { address3 } = this.fields
    return !!address3 && address3.length <= ADDRESS_LENGTH_LIMIT
  }

  isReadFlagValid(): boolean {
    return typeof this.fields.readFlag === 'boolean'
  }
}
